import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";
import type { ConferenceOutputBilling, ConferenceOutputDecision } from "./conferenceOutputPublicService";

// =========================
// Types
// =========================

export interface MasterScopusJournal {
    id: number;
    external_ref_code: number;
    lptk_institution: string;
    journal_name: string;
    journal_url?: string | null;
    quartile: "Q1" | "Q2" | "Q3" | "Q4";
    scope_description?: string | null;
    editor_in_chief?: string | null;
    request_note?: string | null;
    slot_quota?: number | null;
    slot_remaining?: number | null;
    publisher?: string | null;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface MasterSintaJournal {
    id: number;
    external_ref_code: number;
    lptk_institution: string;
    journal_name: string;
    journal_url?: string | null;
    sinta_rank?: string | null;
    scope_description?: string | null;
    editor_in_chief?: string | null;
    request_note?: string | null;
    slot_quota?: number | null;
    slot_remaining?: number | null;
    publisher?: string | null;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface ConferenceOutputFee {
    id: number;
    conference_id: number | null;
    publisher: string;
    price: string;
    currency: "IDR" | "USD";
    service_id: number | null;
    start_date: string | null;
    end_date: string | null;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface ConferenceOutputSetting {
    id: number;
    setting_key: string;
    setting_value: string | null;
    updated_at?: string;
}

export interface ImportBatchErrorEntry {
    sheet?: string;
    row?: number | string;
    reason: string;
}

export interface ImportBatch {
    id: number;
    conference_id: number | null;
    file_name: string;
    import_type: "master_journals" | "journal_mapping" | "proceeding_mapping" | "consolidated_mapping";
    total_rows: number;
    success_rows: number;
    failed_rows: number;
    error_log: ImportBatchErrorEntry[] | null;
    imported_by: number | null;
    created_at: string;
    conference?: { conference_id: number; name: string } | null;
}

interface PaginatedResponse<T> {
    data: T[];
    current_page: number;
    last_page: number;
    total: number;
    per_page: number;
}

interface ApiEnvelope<T> {
    status: string;
    message?: string;
    data: T;
}

const BASE = "/private/conference-output";

// =========================
// Decisions dashboard
// =========================

export interface DecisionFilters {
    conference_id?: number | string;
    output_route?: "journal" | "proceeding";
    author_decision?: "pending" | "agreed" | "declined";
    email_status?: "sent" | "unsent";
    billing_status?: "paid" | "unpaid" | "expired" | "cancelled" | "has_billing" | "no_billing" | "ready_to_bill" | "submitted_unpaid";
    registration_status?: "submitted" | "draft" | "not_started" | "ready_to_bill";
    search?: string;
    per_page?: number;
    page?: number;
}

export const useAdminOutputDecisions = (filters: DecisionFilters = {}, options?: { enabled?: boolean }) => {
    return useQuery({
        queryKey: ["admin-output-decisions", filters],
        queryFn: async () => {
            const { data } = await axiosClient.get<PaginatedResponse<ConferenceOutputDecision>>(`${BASE}/decisions`, { params: filters });
            return data;
        },
        enabled: options?.enabled,
    });
};

/**
 * Download Excel keputusan publikasi sesuai filter yang sedang aktif di halaman
 * (conference_id/track, output_route, author_decision, email_status, billing_status, search) -
 * pola sama seperti exportOutputBillingsExcel, supaya hasil export selalu konsisten
 * dengan data yang sedang ditampilkan admin.
 */
export interface OutputDecisionRecapSummary {
    total_decisions: number;
    total_journal: number;
    total_proceeding: number;
    agreed_journal: number;
    pending_journal: number;
    pending_journal_active: number;
    pending_journal_expired: number;
    declined_to_proceeding: number;
    direct_proceeding: number;

    // Berkas
    camera_ready_uploaded: number;
    copyright_form_uploaded: number;
    both_files_uploaded: number;
    reg_draft: number;
    reg_submitted: number;
    reg_payment_pending: number;
    reg_paid: number;
    reg_not_started: number;

    // Keuangan
    total_billings_count: number;
    total_billed_amount: number;
    paid_count: number;
    paid_amount: number;
    unpaid_count: number;
    unpaid_amount: number;
    expired_count: number;
    expired_amount: number;
    cancelled_count: number;
    cancelled_amount: number;
    ready_to_bill_count: number;
    no_billing_count: number;

    // Email
    email_sent: number;
    email_unsent: number;
    journal_email_sent: number;
    proceeding_email_sent: number;
}

export interface RecapPublisherItem {
    code: string;
    name: string;
    total_papers: number;
    camera_ready_count: number;
    copyright_count: number;
    both_files_count: number;
    billings_count: number;
    paid_count: number;
    paid_amount: number;
    unpaid_count: number;
    unpaid_amount: number;
    expired_count: number;
    expired_amount: number;
    ready_to_bill_count: number;
}

export interface RecapConferenceItem {
    conference_id: number;
    conference_name: string;
    conference_slug: string;
    total_papers: number;
    journal_total: number;
    journal_agreed: number;
    journal_pending: number;
    declined_to_proceeding: number;
    proceeding_total: number;
    camera_ready_count: number;
    both_files_count: number;
    paid_count: number;
    paid_amount: number;
    unpaid_count: number;
    unpaid_amount: number;
    expired_count: number;
    expired_amount: number;
}

export interface RecapScopusQuartileItem {
    quartile: string;
    total: number;
    agreed: number;
    pending: number;
    declined: number;
}

export interface RecapJournalDetailItem {
    id: number;
    journal_name: string;
    quartile?: string;
    sinta_rank?: string;
    editor_in_chief?: string | null;
    total: number;
    agreed: number;
    pending: number;
    declined: number;
}

export interface RecapSintaRankItem {
    sinta_rank: string;
    total: number;
    agreed: number;
    pending: number;
    declined: number;
}

export interface OutputDecisionRecapData {
    summary: OutputDecisionRecapSummary;
    by_publisher: RecapPublisherItem[];
    by_conference: RecapConferenceItem[];
    by_scopus: {
        by_quartile: RecapScopusQuartileItem[];
        journals: RecapJournalDetailItem[];
    };
    by_sinta: {
        by_rank: RecapSintaRankItem[];
        journals: RecapJournalDetailItem[];
    };
}

/**
 * Hook query data rekapitulasi komprehensif untuk rapat pimpinan
 */
export const useAdminOutputDecisionRecap = (conferenceId?: number | string) => {
    return useQuery({
        queryKey: ["admin-output-decision-recap", conferenceId],
        queryFn: async () => {
            const { data } = await axiosClient.get<OutputDecisionRecapData>(`${BASE}/decisions/recap`, {
                params: conferenceId && conferenceId !== "all" ? { conference_id: conferenceId } : undefined,
            });
            return data;
        },
    });
};

/**
 * Download file Excel Rekapitulasi Rapat Eksekutif (multi-sheet)
 */
export const exportOutputDecisionsRecapExcel = async (conferenceId?: number | string) => {
    const params: Record<string, string | number> = {};
    if (conferenceId && conferenceId !== "all") params.conference_id = conferenceId;

    const response = await axiosClient.get(`${BASE}/decisions/export-recap-excel`, {
        params,
        responseType: "blob",
    });

    let filename = `rekap-eksekutif-output-conference-${new Date().toISOString().slice(0, 10)}.xlsx`;
    const contentDisposition = response.headers["content-disposition"];
    if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const exportOutputDecisionsExcel = async (filters: DecisionFilters) => {
    const params: Record<string, string | number> = {};
    if (filters.conference_id) params.conference_id = filters.conference_id;
    if (filters.output_route) params.output_route = filters.output_route;
    if (filters.author_decision) params.author_decision = filters.author_decision;
    if (filters.email_status) params.email_status = filters.email_status;
    if (filters.billing_status) params.billing_status = filters.billing_status;
    if (filters.registration_status) params.registration_status = filters.registration_status;
    if (filters.search) params.search = filters.search;

    const response = await axiosClient.get(`${BASE}/decisions/export-excel`, {
        params,
        responseType: "blob",
    });

    let filename = `keputusan-publikasi-output-conference-${new Date().toISOString().slice(0, 10)}.xlsx`;
    const contentDisposition = response.headers["content-disposition"];
    if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const useAdminOutputDecision = (id?: number | string) => {
    return useQuery({
        queryKey: ["admin-output-decision", id],
        queryFn: async () => {
            const { data } = await axiosClient.get<ConferenceOutputDecision>(`${BASE}/decisions/${id}`);
            return data;
        },
        enabled: !!id,
    });
};

export interface UpdateDecisionPayload {
    scopus_journal_id?: number | null;
    sinta_journal_id?: number | null;
    journal_admin_note?: string | null;
    decision_deadline?: string | null;
}

export const useUpdateOutputDecision = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: UpdateDecisionPayload }) => {
            const { data } = await axiosClient.put(`${BASE}/decisions/${id}`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
            queryClient.invalidateQueries({ queryKey: ["admin-output-decision"] });
        },
    });
};

export interface CancelDecisionSummary {
    has_camera_ready: boolean;
    has_copyright_form: boolean;
    has_billing: boolean;
    billing_status?: string | null;
    billing_invoice_number?: string | null;
}

export interface CancelDecisionResponse {
    status: "success" | "confirm_required" | "error";
    message: string;
    summary?: CancelDecisionSummary;
    data?: unknown;
}

/**
 * Batalkan keputusan penulis (balik ke pending). Kalau sudah ada file terunggah/tagihan,
 * backend menolak dengan status 'confirm_required' (bukan throw) berisi `summary` supaya FE bisa
 * tampilkan dialog konfirmasi sebelum memanggil ulang dengan force:true.
 */
export const useCancelOutputDecision = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, force }: { id: number; force?: boolean }) => {
            const { data } = await axiosClient.post<CancelDecisionResponse>(`${BASE}/decisions/${id}/cancel`, { force: !!force });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
            queryClient.invalidateQueries({ queryKey: ["admin-output-decision"] });
        },
    });
};

export const useGenerateOutputDecisionInvoice = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, send_email = true }: { id: number; send_email?: boolean }) => {
            const { data } = await axiosClient.post(`${BASE}/decisions/${id}/generate-invoice`, { send_email });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
            queryClient.invalidateQueries({ queryKey: ["admin-output-decision"] });
            queryClient.invalidateQueries({ queryKey: ["admin-output-billings"] });
        },
    });
};

export const useSendOutputDecisionEmail = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (arg: number | { id: number; email_template_type?: "regular" | "reminder"; override_email?: string }) => {
            const id = typeof arg === "number" ? arg : arg.id;
            const payload = typeof arg === "number" ? {} : { email_template_type: arg.email_template_type, override_email: arg.override_email };
            const { data } = await axiosClient.post(`${BASE}/decisions/${id}/send-email`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
        },
    });
};

export interface BulkEmailPayload {
    conference_id?: number | string;
    output_route?: "journal" | "proceeding";
    billing_status?: string;
    email_template_type?: "regular" | "reminder";
    force?: boolean;
    limit?: number;
}

export const useSendOutputBulkEmail = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: BulkEmailPayload) => {
            const { data } = await axiosClient.post(`${BASE}/decisions/send-bulk-email`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
        },
    });
};

export const useExtendSubmissionDeadline = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, new_deadline, reason }: { id: number; new_deadline: string; reason?: string }) => {
            const { data } = await axiosClient.post(`${BASE}/decisions/${id}/extend-submission-deadline`, { new_deadline, reason });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
            queryClient.invalidateQueries({ queryKey: ["admin-output-decision"] });
        },
    });
};

export interface BulkExtendDeadlinePayload {
    conference_id?: number | string;
    output_route?: "all" | "journal" | "proceeding";
    author_decision?: "all" | "pending" | "agreed" | "declined";
    billing_status?: string;
    new_deadline: string;
    send_email?: boolean;
    email_template_type?: "regular" | "reminder";
    limit?: number;
}

export const useBulkExtendOutputDecisionDeadline = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: BulkExtendDeadlinePayload) => {
            const { data } = await axiosClient.post(`${BASE}/decisions/bulk-extend-deadline`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
        },
    });
};

export interface TestSendEmailPayload {
    decision_id?: number;
    email_template_type?: "regular" | "reminder";
    target_email?: string;
}

export const useTestSendOutputDecisionEmail = () => {
    return useMutation({
        mutationFn: async (payload: TestSendEmailPayload) => {
            const { data } = await axiosClient.post(`${BASE}/decisions/test-send-email`, payload);
            return data;
        },
    });
};

// =========================
// Import: master journals, journal/proceeding/consolidated mapping, batches
// =========================

export const useImportMasterJournals = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (formData: FormData) => {
            const { data } = await axiosClient.post(`${BASE}/import/master-journals`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-master-scopus"] });
            queryClient.invalidateQueries({ queryKey: ["admin-master-sinta"] });
            queryClient.invalidateQueries({ queryKey: ["admin-import-batches"] });
        },
    });
};

export const useImportJournalMapping = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ conference_id, formData }: { conference_id: number | string; formData: FormData }) => {
            const { data } = await axiosClient.post(`${BASE}/import/journal-mapping/${conference_id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
            queryClient.invalidateQueries({ queryKey: ["admin-import-batches"] });
        },
    });
};

export const useImportProceedingMapping = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ conference_id, formData }: { conference_id: number | string; formData: FormData }) => {
            const { data } = await axiosClient.post(`${BASE}/import/proceeding-mapping/${conference_id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
            queryClient.invalidateQueries({ queryKey: ["admin-import-batches"] });
        },
    });
};

export const useImportConsolidatedMapping = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (formData: FormData) => {
            const { data } = await axiosClient.post(`${BASE}/import/consolidated-mapping`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-decisions"] });
            queryClient.invalidateQueries({ queryKey: ["admin-import-batches"] });
        },
    });
};

export const useAdminImportBatches = (filters: { import_type?: string; per_page?: number; page?: number } = {}) => {
    return useQuery({
        queryKey: ["admin-import-batches", filters],
        queryFn: async () => {
            const { data } = await axiosClient.get<PaginatedResponse<ImportBatch>>(`${BASE}/import/batches`, { params: filters });
            return data;
        },
    });
};

// Template downloads - dipakai lewat window.open / anchor href langsung (butuh Bearer token via axios blob)
export const downloadOutputTemplate = async (kind: "master-journals" | "journal-mapping" | "proceeding-mapping" | "consolidated-mapping", filename: string) => {
    const response = await axiosClient.get(`${BASE}/templates/${kind}`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// =========================
// Fees CRUD
// =========================

export const useAdminOutputFees = () => {
    return useQuery({
        queryKey: ["admin-output-fees"],
        queryFn: async () => {
            const { data } = await axiosClient.get<ApiEnvelope<ConferenceOutputFee[]> | ConferenceOutputFee[]>(`${BASE}/fees`);
            return Array.isArray(data) ? data : data.data;
        },
    });
};

export const useCreateOutputFee = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: Partial<ConferenceOutputFee>) => {
            const { data } = await axiosClient.post(`${BASE}/fees`, payload);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-fees"] }),
    });
};

export const useUpdateOutputFee = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<ConferenceOutputFee> }) => {
            const { data } = await axiosClient.put(`${BASE}/fees/${id}`, payload);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-fees"] }),
    });
};

export const useDeleteOutputFee = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`${BASE}/fees/${id}`);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-fees"] }),
    });
};

// =========================
// Billings (admin monitoring + extend)
// =========================

export interface ConferenceOutputBillingFilters {
    status?: string;
    per_page?: number;
    page?: number;
    date_from?: string;
    date_to?: string;
    conference_id?: string;
    search?: string;
}

export interface ConferenceOutputBillingsResponse extends PaginatedResponse<ConferenceOutputBilling> {
    sum_amount: number;
}

export const useAdminOutputBillings = (filters: ConferenceOutputBillingFilters = {}) => {
    return useQuery({
        queryKey: ["admin-output-billings", filters],
        queryFn: async () => {
            const { data } = await axiosClient.get<ConferenceOutputBillingsResponse>(`${BASE}/billings`, { params: filters });
            return data;
        },
    });
};

/**
 * Download Excel tagihan sesuai filter status + rentang tanggal (created_at).
 * Trigger via anchor <a download> dari blob - pola sama seperti export lain di proyek ini
 * (lihat useExportPeriodExcel.ts / ExportButtons.tsx).
 */
export const exportOutputBillingsExcel = async (filters: { status?: string; date_from?: string; date_to?: string; conference_id?: string }) => {
    const params: Record<string, string> = {};
    if (filters.status && filters.status !== "all") params.status = filters.status;
    if (filters.conference_id && filters.conference_id !== "all") params.conference_id = filters.conference_id;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;

    const response = await axiosClient.get(`${BASE}/billings/export-excel`, {
        params,
        responseType: "blob",
    });

    let filename = `tagihan-publikasi-prosiding-${new Date().toISOString().slice(0, 10)}.xlsx`;
    const contentDisposition = response.headers["content-disposition"];
    if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
    }

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const useAdminOutputBilling = (id?: number | string) => {
    return useQuery({
        queryKey: ["admin-output-billing", id],
        queryFn: async () => {
            const { data } = await axiosClient.get<ConferenceOutputBilling>(`${BASE}/billings/${id}`);
            return data;
        },
        enabled: !!id,
    });
};

export const useExtendOutputBilling = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, new_expired_at, reason }: { id: number; new_expired_at: string; reason?: string }) => {
            const { data } = await axiosClient.post(`${BASE}/billings/${id}/extend`, { new_expired_at, reason });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-billings"] });
            queryClient.invalidateQueries({ queryKey: ["admin-output-billing"] });
        },
    });
};

export interface BulkExtendBillingsPayload {
    conference_id?: number | string;
    status?: "all" | "unpaid" | "expired";
    new_expired_at: string;
    reason?: string;
    send_email?: boolean;
    limit?: number;
}

export interface BulkExtendBillingsResponse {
    status: "success" | "error";
    message: string;
    total_matching: number;
    total_extended: number;
    failed_count: number;
    emails_sent: number;
    emails_failed: number;
    errors?: Array<{ id: number; invoice_number: string; error: string }>;
}

export const useBulkExtendOutputBillings = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: BulkExtendBillingsPayload) => {
            const { data } = await axiosClient.post<BulkExtendBillingsResponse>(`${BASE}/billings/bulk-extend`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-billings"] });
        },
    });
};

// --- Sync massal ke Smart Payment (pola sama seperti /periods/{id}/payment/sync-status) ---
export interface OutputSyncCheckedDetail {
    customer_number: string | number;
    buyer_name?: string | null;
    participant_count: number;
    previous_status: string;
    new_status?: string | null;
    updated: boolean;
    error?: string | null;
}

export interface OutputSyncPaymentResponse {
    message?: string;
    total_va_checked?: number;
    total_participant_updated?: number;
    failed_count?: number;
    failed_customer_numbers?: (string | number)[];
    checked_details?: OutputSyncCheckedDetail[];
}

export const useSyncOutputBillings = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            const { data } = await axiosClient.get<OutputSyncPaymentResponse>(`${BASE}/billings/sync-status`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-output-billings"] });
        },
    });
};

// =========================
// Settings
// =========================

export const useAdminOutputSettings = () => {
    return useQuery({
        queryKey: ["admin-output-settings"],
        queryFn: async () => {
            const { data } = await axiosClient.get<ApiEnvelope<ConferenceOutputSetting[]> | ConferenceOutputSetting[]>(`${BASE}/settings`);
            return Array.isArray(data) ? data : data.data;
        },
    });
};

export const useUpdateOutputSetting = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ key, value }: { key: string; value: string }) => {
            const { data } = await axiosClient.put(`${BASE}/settings/${key}`, { setting_value: value });
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-settings"] }),
    });
};

// =========================
// Master Scopus CRUD
// =========================

export const useAdminMasterScopus = (search?: string) => {
    return useQuery({
        queryKey: ["admin-master-scopus", search ?? ""],
        placeholderData: (prev) => prev,
        queryFn: async () => {
            // Endpoint ini dipaginasi server (default 20/halaman) - minta per_page besar supaya seluruh
            // master jurnal termuat sekaligus, karena UI di atasnya paginasi & hitung kartu total di client.
            const { data } = await axiosClient.get<PaginatedResponse<MasterScopusJournal> | MasterScopusJournal[]>(`${BASE}/master-scopus`, { params: { per_page: 1000, search: search || undefined } });
            return Array.isArray(data) ? data : data.data;
        },
    });
};

export const useCreateMasterScopus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: Partial<MasterScopusJournal>) => {
            const { data } = await axiosClient.post(`${BASE}/master-scopus`, payload);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-master-scopus"] }),
    });
};

export const useUpdateMasterScopus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<MasterScopusJournal> }) => {
            const { data } = await axiosClient.put(`${BASE}/master-scopus/${id}`, payload);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-master-scopus"] }),
    });
};

export const useDeleteMasterScopus = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`${BASE}/master-scopus/${id}`);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-master-scopus"] }),
    });
};

// =========================
// Master Sinta CRUD
// =========================

export const useAdminMasterSinta = (search?: string) => {
    return useQuery({
        queryKey: ["admin-master-sinta", search ?? ""],
        placeholderData: (prev) => prev,
        queryFn: async () => {
            const { data } = await axiosClient.get<PaginatedResponse<MasterSintaJournal> | MasterSintaJournal[]>(`${BASE}/master-sinta`, { params: { per_page: 1000, search: search || undefined } });
            return Array.isArray(data) ? data : data.data;
        },
    });
};

export const useCreateMasterSinta = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: Partial<MasterSintaJournal>) => {
            const { data } = await axiosClient.post(`${BASE}/master-sinta`, payload);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-master-sinta"] }),
    });
};

export const useUpdateMasterSinta = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<MasterSintaJournal> }) => {
            const { data } = await axiosClient.put(`${BASE}/master-sinta/${id}`, payload);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-master-sinta"] }),
    });
};

export const useDeleteMasterSinta = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`${BASE}/master-sinta/${id}`);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-master-sinta"] }),
    });
};

// =========================
// Publishers (KNE, SHS, dst) - nama tampilan, website, file template naskah & author guideline
// =========================

export interface ConferenceOutputPublisherAdmin {
    id: number;
    code: string;
    display_name: string;
    website_url: string | null;
    template_file_path: string | null;
    guideline_file_path: string | null;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export const useAdminOutputPublishers = () => {
    return useQuery({
        queryKey: ["admin-output-publishers"],
        queryFn: async () => {
            const { data } = await axiosClient.get<ConferenceOutputPublisherAdmin[]>(`${BASE}/publishers`);
            return data;
        },
    });
};

export const useCreateOutputPublisher = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: { code: string; display_name: string; website_url?: string | null; is_active?: boolean }) => {
            const { data } = await axiosClient.post(`${BASE}/publishers`, payload);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-publishers"] }),
    });
};

export const useUpdateOutputPublisher = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: Partial<Pick<ConferenceOutputPublisherAdmin, "display_name" | "website_url" | "is_active">> }) => {
            const { data } = await axiosClient.put(`${BASE}/publishers/${id}`, payload);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-publishers"] }),
    });
};

export const useDeleteOutputPublisher = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`${BASE}/publishers/${id}`);
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-publishers"] }),
    });
};

export const useUploadPublisherTemplate = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, file }: { id: number; file: File }) => {
            const formData = new FormData();
            formData.append("template_file", file);
            const { data } = await axiosClient.post(`${BASE}/publishers/${id}/upload-template`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-publishers"] }),
    });
};

export const useUploadPublisherGuideline = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, file }: { id: number; file: File }) => {
            const formData = new FormData();
            formData.append("guideline_file", file);
            const { data } = await axiosClient.post(`${BASE}/publishers/${id}/upload-guideline`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            return data;
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin-output-publishers"] }),
    });
};
